import { isMobileBreakpoint, mediaQueryLarge, throttle, updateHeaderHeights } from '@theme/utilities';

const HEADER_CLASSES = {
  IS_TRANSPARENT: 'is-transparent',
  IS_SCROLLED: 'is-scrolled',
};

const VALUES = {
  SCROLL_THRESHOLD: 10,
  SCROLL_THRESHOLD_MOBILE: 22,
  THROTTLE_DELAY: 15,
  MOUSE_ENTER_LEAVE_DELAY: 300,
};

class HeaderMain extends HTMLElement {
  /** Whether the header has transparent mode enabled (set via attribute at init) */
  #supportsTransparent = false;

  /** Current transparent state (changes as user scrolls) */
  #isTransparent = false;

  /** @type {boolean} */
  #isListening = false;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** Cached pre-header offsetHeight — updated by ResizeObserver */
  #preHeaderHeight = 0;

  /** Tracks whether pre-header was visible on last scroll tick — detects fast-scroll threshold crossing */
  #wasPreHeaderVisible = true;

  /** @type {HTMLElement | null} */
  #filterBarEl = null;

  /** @type {boolean} */
  #isMobile = false;

  connectedCallback() {
    this.#supportsTransparent = this.hasAttribute(HEADER_CLASSES.IS_TRANSPARENT);
    this.#isTransparent = this.#supportsTransparent;
    this.#isMobile = isMobileBreakpoint();

    updateHeaderHeights();
    this.#setupResizeObserver();
    this.#wasPreHeaderVisible = window.scrollY < this.#preHeaderHeight;
    this.#setupScrollListener();
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
  }

  disconnectedCallback() {
    if (this.#isListening) {
      this.#isListening = false;
      window.removeEventListener('scroll', this.#throttledHandleScroll);
    }
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  #setupResizeObserver() {
    const preHeader = document.querySelector('#pre-header');
    this.#resizeObserver = new ResizeObserver(() => {
      if (preHeader instanceof HTMLElement) {
        this.#preHeaderHeight = preHeader.offsetHeight;
      }
      updateHeaderHeights();
    });
    this.#resizeObserver.observe(this);
    if (preHeader instanceof HTMLElement) {
      this.#preHeaderHeight = preHeader.offsetHeight;
      this.#resizeObserver.observe(preHeader);
    }
  }

  #setupScrollListener() {
    if (this.#isListening) return;
    this.#isListening = true;
    window.addEventListener('scroll', this.#throttledHandleScroll, { passive: true });
  }

  #handleScroll = () => {
    // Write first: apply is-scrolled attribute before reading layout
    const stateChanged = this.#updateHeaderState();

    const isPreHeaderVisible = window.scrollY < this.#preHeaderHeight;
    const crossedThreshold = isPreHeaderVisible !== this.#wasPreHeaderVisible;

    if (isPreHeaderVisible || crossedThreshold || stateChanged) {
      this.#wasPreHeaderVisible = isPreHeaderVisible;
      updateHeaderHeights();
    }
  };

  #handleMediaQueryChange = () => {
    this.#isMobile = isMobileBreakpoint();
  };

  #throttledHandleScroll = throttle(this.#handleScroll, VALUES.THROTTLE_DELAY);

  #updateHeaderState = () => {
    const shouldBeTransparent = window.scrollY < VALUES.SCROLL_THRESHOLD;

    if (shouldBeTransparent) {
      this.removeAttribute(HEADER_CLASSES.IS_SCROLLED);
    } else {
      this.setAttribute(HEADER_CLASSES.IS_SCROLLED, '');
    }

    this.#updateFilterBarState();

    if (!this.#supportsTransparent) return false;

    if (shouldBeTransparent === this.#isTransparent) return false;

    this.#isTransparent = shouldBeTransparent;

    return true;
  };

  #updateFilterBarState() {
    const filterBarEl = this.#getFilterBarEl();

    if (!filterBarEl) return;

    const filterBarOffsetTop = filterBarEl.getBoundingClientRect().top;
    const scrollThreshold = this.#isMobile ? VALUES.SCROLL_THRESHOLD_MOBILE : VALUES.SCROLL_THRESHOLD;

    if (filterBarOffsetTop < scrollThreshold && filterBarOffsetTop > 0) {
      filterBarEl.setAttribute(HEADER_CLASSES.IS_SCROLLED, '');
    } else {
      filterBarEl.removeAttribute(HEADER_CLASSES.IS_SCROLLED);
    }
  }

  #getFilterBarEl() {
    // Re-query the element if it's undefined
    if (this.#filterBarEl?.isConnected) return this.#filterBarEl;
    this.#filterBarEl = document.querySelector('.search__topbar');
    return this.#filterBarEl;
  }
}

if (!customElements.get('header-main')) {
  customElements.define('header-main', HeaderMain);
}
